import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { clearFields } from 'redux-form'
import { useDispatch } from 'react-redux'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useApolloClient } from 'hooks/useApolloClient'
import { useOfflineFieldUpsert } from 'hooks/offline'
// constants
import { Forms } from 'constants/forms'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
// graphql
import {
  FIELD_CREATE_MUTATION,
  FIELD_DELETE_MUTATION,
  FIELD_UPDATE_MUTATION,
  FIELD_VALUE_DELETE_MUTATION,
  FIELD_VALUE_UPSERT_MUTATION,
  FIELD_VALUE_DELETE_PB_MUTATION,
  FIELD_VALUE_UPSERT_PB_MUTATION,
  FIELD_UPDATE_POSITIONS_MUTATION
} from 'graphql/objects/mutations'
import {
  FIELD_LINK_DELETE_MUTATION,
  FIELD_GROUP_DELETE_MUTATION,
  FIELD_LINK_BUNCH_UPDATE_MUTATION
} from 'graphql/field/mutations'
import { RISK_TEMPLATE_UPDATE_MUTATION } from 'graphql/risk/mutations'
// types
import { IOption } from 'components/FormElements/Select'
import { TRefetch } from 'types/refetch'
import { ENTITIES } from 'constants/entities'
import {
  IField,
  IRiskTemplate,
  IFieldResponse,
  IFieldLinkInput,
  IMutationResponse,
  IRiskTemplatesQueryArgs,
  IFieldCreateMutationArgs,
  IFieldDeleteMutationArgs,
  IFieldUpdateMutationArgs,
  IFieldUpdatePositionInput,
  IFieldValueDeleteMutationArgs,
  IFieldValueUpsertMutationArgs,
  IRiskTemplateUpdateMutationArgs,
  IFieldLinkBunchDeleteMutationArgs,
  IFieldLinkBunchUpdateMutationArgs,
  IFieldUpdatePositionsMutationArgs,
  IFieldGroupBunchDeleteMutationArgs
} from 'types/graphql'
import { NRiskTemplateEditAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import { get, omit, orderBy } from 'lodash'
import { encodeHtml, getTextFromHtml } from 'utils/html'
import { getFieldSettings, normalizeFieldProperty } from 'utils/fields'
import { getValueFromOption, getValuesFromOptions, parseGraphQLErrorMessages } from 'utils'

export const useRiskTemplateAPI = ({
  fields,
  refetch,
  riskTemplate
}: {
  fields: IField[]
  refetch: TRefetch<any, IRiskTemplatesQueryArgs>
  riskTemplate: IRiskTemplate
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const isPublic = React.useContext(PublicContext)

  const {
    permissionsFromSecurityGroups: { aiManageSettings }
  } = useCurrentUser()

  const { client } = useApolloClient()

  const updateFieldValuesOffline = useOfflineFieldUpsert()

  const updateRiskTemplate: NRiskTemplateEditAPIContext.IContext['updateRiskTemplate'] = async (input) => {
    try {
      await client.mutate<IMutationResponse, IRiskTemplateUpdateMutationArgs>({
        mutation: RISK_TEMPLATE_UPDATE_MUTATION,
        variables: { id: riskTemplate.id, input }
      })

      await refetch()
    } catch (error) {
      console.log('[RiskTemplateAPI.updateRiskTemplate]: ', parseGraphQLErrorMessages(error))
    }
  }

  const createField: NRiskTemplateEditAPIContext.IContext['createField'] = async (field) => {
    try {
      const response = await client.mutate<Record<'fieldCreate', IFieldResponse>, IFieldCreateMutationArgs>({
        mutation: FIELD_CREATE_MUTATION,
        variables: { input: field }
      })

      await refetch()

      return response.data.fieldCreate.data
    } catch (errors) {
      console.log('[RiskTemplateAPI.createField]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const updateField: NRiskTemplateEditAPIContext.IContext['updateField'] = async (data, field) => {
    try {
      const source = data.source?.filter((item: any) => item.entity !== ENTITIES.RISK).map(getValueFromOption)
      const searchTemplateIds = data.source
        ?.filter((item: any) => item.entity === ENTITIES.RISK)
        .map(getValueFromOption)

      const isWysiwygDisabled = field.wysiwyg && !data.wysiwyg
      const isWysiwygEnabled = !field.wysiwyg && data.wysiwyg

      await client.mutate<IFieldResponse, IFieldUpdateMutationArgs>({
        mutation: FIELD_UPDATE_MUTATION,
        variables: {
          id: field.id,
          input: {
            active: data.active,
            fieldType: field.fieldType,
            format: normalizeFieldProperty([data.format]),
            label: isWysiwygDisabled ? getTextFromHtml(data.name) : isWysiwygEnabled ? encodeHtml(data.name) : data.name,
            hidden: data.hidden,
            backgroundColor: data.backgroundColor,
            parentFieldId: data.parentFieldId,
            position: data.position ?? field.position,
            historyEnabled: data.historyEnabled,
            displayEmail: data.displayEmail,
            multiline: !!data.multiline,
            multiple: !!data.multiple,
            objectEntityId: field.objectEntityId,
            options: data.options?.map((option: IOption) => omit(option, '__typename')),
            required: data.required,
            standard: field.standard,
            searchable: data.searchable,
            wysiwyg: data.wysiwyg,
            readonly: data.readonly,
            responseType: data.responseType,
            hideName: data.hideName,
            isPublicOnly: data.isPublicOnly,
            showUserDetails: data.showUserDetails,
            visibleUserDetailsFields: getValuesFromOptions(data.visibleUserDetailsFields),
            hideOnPublic: data.hideOnPublic,
            exportHeaders: data.exportHeaders,
            includeHistoryInApiResponse: data.includeHistoryInApiResponse,
            weight: Number(data.weight),
            responseTypeId: getValueFromOption(data.responseTypeId),
            objectSecurityId: getValueFromOption(data.objectSecurity),
            settings: getFieldSettings(data, field.fieldType),
            source,
            searchTemplateIds,
            ...(aiManageSettings && {
              includeInAiContext: data.includeInAiContext,
              aiAutoPopulate: data.aiAutoPopulate,
              aiIncludeHistory: data.aiIncludeHistory,
              aiContextDescription: data.aiContextDescription,
              aiSuggestedPrompts: data.aiSuggestedPrompts?.filter((prompt: string) => !!prompt?.trim())
            })
          }
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[RiskTemplateAPI.updateField]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const deleteField: NRiskTemplateEditAPIContext.IContext['deleteField'] = async (field) => {
    try {
      await client.mutate<IMutationResponse, IFieldDeleteMutationArgs>({
        mutation: FIELD_DELETE_MUTATION,
        variables: { id: field.id }
      })

      if (field.parentFieldId) {
        await updateTableFieldsPositions({ fieldId: field.id, parentFieldId: field.parentFieldId })
      } else {
        await updateFieldsPositions(field.id)
      }

      await refetch()

      dispatch(clearFields(Forms.RISK_TEMPLATE_FORM, false, false, field.id))
    } catch (errors) {
      console.log('[RiskTemplateAPI.deleteField]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const deleteForm: NRiskTemplateEditAPIContext.IContext['deleteForm'] = async (fieldLink) => {
    try {
      if (fieldLink.primary) {
        const fieldGroupsIds = riskTemplate.fieldGroups
          .filter((item) => item.fieldId === fieldLink.field.id && item.parentFieldId === fieldLink.parentFieldId)
          .map(({ id }) => ({ id }))

        await client.mutate<IMutationResponse, IFieldGroupBunchDeleteMutationArgs>({
          mutation: FIELD_GROUP_DELETE_MUTATION,
          variables: { input: fieldGroupsIds }
        })

        await client.mutate<IMutationResponse, IFieldLinkBunchDeleteMutationArgs>({
          mutation: FIELD_LINK_DELETE_MUTATION,
          variables: {
            input: [{ id: fieldLink.id }]
          }
        })
      } else {
        await client.mutate<IMutationResponse, IFieldLinkBunchDeleteMutationArgs>({
          mutation: FIELD_LINK_DELETE_MUTATION,
          variables: { input: [{ id: fieldLink.id }] }
        })
      }

      const {
        data: { riskTemplates }
      } = await refetch()

      await updateTableFieldsPositions({ data: get(riskTemplates, 'data[0]'), parentFieldId: fieldLink.parentFieldId })
    } catch (errors) {
      console.log('[RiskTemplateAPI.deleteForm]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const reorderFields: NRiskTemplateEditAPIContext.IContext['reorderFields'] = async (
    fieldsOrderInput,
    fieldLinksOrderInput
  ) => {
    try {
      if (fieldsOrderInput?.length) {
        await client.mutate<IMutationResponse, IFieldUpdatePositionsMutationArgs>({
          mutation: FIELD_UPDATE_POSITIONS_MUTATION,
          variables: { input: fieldsOrderInput }
        })
      }

      if (fieldLinksOrderInput?.length) {
        await client.mutate<IMutationResponse, IFieldLinkBunchUpdateMutationArgs>({
          mutation: FIELD_LINK_BUNCH_UPDATE_MUTATION,
          variables: { input: fieldLinksOrderInput }
        })
      }

      await refetch()
    } catch (errors) {
      console.log('[RiskTemplateAPI.reorderFields]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const updateFieldsPositions = async (fieldId: string) => {
    try {
      const fieldsOrderInput = orderBy(
        fields.filter(({ parentFieldId }) => !parentFieldId),
        'position',
        'asc'
      )
        .filter(({ id }) => id !== fieldId)
        .map((field, idx) => ({
          id: field.id,
          position: idx
        }))

      await reorderFields(fieldsOrderInput)
    } catch (errors) {
      console.log('[RiskTemplateAPI.updateFieldsPositions]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const updateTableFieldsPositions = async ({
    data = riskTemplate,
    fieldId,
    parentFieldId
  }: {
    data?: IRiskTemplate
    fieldId?: string
    parentFieldId?: string
  }) => {
    try {
      const fieldsOrderInput: IFieldUpdatePositionInput[] = []
      const fieldLinksOrderInput: IFieldLinkInput[] = []

      const fields = orderBy(
        [
          ...data.fields.filter((item) => item.parentFieldId === parentFieldId),
          ...data.fieldLinks
            .filter((item) => item.parentFieldId === parentFieldId)
            .map((fieldLink) => ({
              ...fieldLink.field,
              parentFieldId: fieldLink.parentFieldId,
              position: fieldLink.position,
              label: fieldLink.fieldLabel
            }))
        ],
        'position',
        'asc'
      )
        .filter((field) => (fieldId ? field.id !== fieldId : true))
        .map((field, idx) => ({
          id: field.id,
          position: idx
        }))

      fields.forEach((field) => {
        const fieldLink = data.fieldLinks.find(
          (item) => item.field.id === field.id && item.parentFieldId === parentFieldId
        )

        if (fieldLink) {
          const { id, targetEntityId } = fieldLink
          fieldLinksOrderInput.push({ id, fieldId: field.id, targetEntityId, position: field.position })
        } else {
          fieldsOrderInput.push(field)
        }
      })

      await reorderFields(fieldsOrderInput, fieldLinksOrderInput)
    } catch (errors) {
      console.log('[RiskTemplateAPI.updateTableFieldsPositions]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const updateFieldValues: NRiskTemplateEditAPIContext.IContext['updateFieldValues'] = async (
    data,
    entityId,
    field
  ) => {
    try {
      const variables = {
        entityId: entityId || (router.query.id as string),
        input: data,
        ...(router.query.domain && { domain: router.query.domain })
      }

      if (!navigator.onLine) {
        return updateFieldValuesOffline(variables, field)
      }

      return await client.mutate<IMutationResponse, IFieldValueUpsertMutationArgs>({
        mutation: isPublic ? FIELD_VALUE_UPSERT_PB_MUTATION : FIELD_VALUE_UPSERT_MUTATION,
        variables
      })
    } catch (errors) {
      console.log('[RiskTemplateAPI.updateFieldValues]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const deleteFieldValues: NRiskTemplateEditAPIContext.IContext['deleteFieldValues'] = async (ids) => {
    try {
      await client.mutate<IMutationResponse, IFieldValueDeleteMutationArgs>({
        mutation: isPublic ? FIELD_VALUE_DELETE_PB_MUTATION : FIELD_VALUE_DELETE_MUTATION,
        variables: {
          ids,
          ...(router.query.domain && { domain: router.query.domain })
        }
      })
    } catch (errors) {
      console.log('[RiskTemplateAPI.deleteFieldValues]: ', parseGraphQLErrorMessages(errors))
    }
  }

  return {
    deleteForm,
    createField,
    updateField,
    deleteField,
    reorderFields,
    updateFieldValues,
    deleteFieldValues,
    updateRiskTemplate
  }
}
